import { CheckCircle } from '@mui/icons-material'
import {
  Box,
  Button,
  lighten,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'

import { ABC_FITNESS_AUTH_FEATURES_LINK } from '~/features/auth/constants'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { ESiteImagesTypes } from '~/features/partners/models'
import { getImgUrlByType } from '~/features/partners/utils'
import rem from '~/utils/ui'

const ABC_FITNESS_AUTH_FEATURES = [
  'Add hundreds of qualified leads in a quarter with no added headcount',
  'Capture nearly 10X more leads in peak months',
  'Move prospects from inquiry to membership in 3-5 days instead of 30',
  'Save 180 staff hours a month on repetitive calls',
  'Call every new lead the moment it comes in',
]

function AbcFitnessAuthFeaturesPanel() {
  const theme = useTheme()
  const { partner } = usePartnerCentralized()
  const logoSrc = getImgUrlByType(ESiteImagesTypes.logoLight, partner)

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.common.black,
        width: '100%',
        color: theme.palette.common.white,
        overflow: 'auto',
      }}
    >
      <Stack
        sx={{
          width: '100%',
          maxWidth: rem(580),
          p: { xxxs: 4, md: rem(48) },
          gap: { xxxs: 3, md: 4 },
          alignItems: 'flex-start',
        }}
      >
        {logoSrc && (
          <Box
            component="img"
            src={logoSrc}
            alt={partner?.partnerName ?? 'logo'}
            sx={{
              width: { xxxs: rem(120), md: rem(180) },
            }}
          />
        )}
        <Stack gap={{ xxxs: 2, md: 3 }} sx={{ width: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.primary1,
              letterSpacing: rem(1),
              textTransform: 'uppercase',
            }}
          >
            NEW TO ABC AI AGENTS?
          </Typography>
          <Typography
            component="h2"
            fontWeight={theme.typography.fontWeightBold}
            sx={{
              fontSize: { xxxs: rem(28), md: rem(40) },
              lineHeight: 1.15,
            }}
          >
            Stop losing leads to missed calls
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[400],
              lineHeight: 1.5,
            }}
          >
            Every call, text, and chat gets answered, day or night, by agents
            integrated with the ABC platform you already run on.
          </Typography>
        </Stack>
        <Box sx={{ width: '100%' }}>
          <List disablePadding>
            {ABC_FITNESS_AUTH_FEATURES.map((feature) => (
              <ListItem
                disablePadding
                key={feature}
                sx={{
                  alignItems: 'flex-start',
                  py: rem(8),
                  '& .MuiListItemIcon-root': {
                    minWidth: rem(32),
                    mt: rem(2),
                  },
                }}
              >
                <ListItemIcon
                  sx={{ color: lighten(theme.palette.tertiary1, 0.4) }}
                >
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    variant: 'body1',
                    sx: { lineHeight: 1.4 },
                  }}
                  sx={{ margin: 0 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Button
          href={ABC_FITNESS_AUTH_FEATURES_LINK}
          sx={{
            '&&&&': {
              backgroundColor: lighten(theme.palette.tertiary1, 0.4),
              color: theme.palette.common.black,
            },
          }}
          variant="primaryModern"
          target="_blank"
        >
          Book a demo
        </Button>
      </Stack>
    </Box>
  )
}

export default AbcFitnessAuthFeaturesPanel
